/*    
<summary>
  This functional component "AddStoreDetailsForm" provides form to update store  and store can view its details .
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik, FormikProps, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../../../contexts/store-provider";
import { IOption } from "../../../../models/ICommon";
import {
  UpdateStoreDetailsValidationSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import FormikFormImagePicker from "../../../../shared-components/FormikFormHandlers/FormikFormImagePicker";
import {
  integerNumJpRegex,
  integerRegex
} from "../../../../constants/constants";
import FormikFormInputForRegexCheck from "../../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputMobileNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import FormikFormInputAddress from "../../../../shared-components/FormikFormHandlers/FormikFormInputAddress";
import LoaderButton from "../../../../shared-components/button/loader-button";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import FormikFormPasswordInput from "../../../../shared-components/FormikFormHandlers/FormikFormPasswordInput";
import { FormObserver } from "../../../../shared-components/address/form-observer";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  getStoreManagementLoading: boolean;
  addUpdateStoreManagementLoading: boolean;
  initialValues: any;
  businessTypeOptions: IOption[];
  showDeletePopup: boolean;
  onDeleteModalClosed: () => void;
  storeCategoryOptions: IOption[];
}
const StoreDetailsForm = (props: IProps) => {
  const formikRef = useRef<FormikProps<any>>(null);
  const { storeManagementStore } = useStore();
  const { GetPrefectureUsingPostalCode, prefectureByZipCode } =
    storeManagementStore;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [postalCode, setPostalCode] = useState<string>("");
  const [prefectureName, setPrefectureName] = useState<string>("");
  const [address12, setAddress12] = useState<string>("");

  useEffect(() => {
    if (postalCode && postalCode?.length == 7) {
      GetPrefectureUsingPostalCode(postalCode);
    }
  }, [postalCode]);

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          ...props.initialValues,
          Password: "",
          ConfirmPassword: "",
        }}
        enableReinitialize={true}
        validationSchema={UpdateStoreDetailsValidationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={async (values) => {
          let tempData = cloneDeep(values);
          tempData.PostalCode = toHalfWidth(values.PostalCode);
          tempData.PrefectureName = values.PrefectureName;
          tempData.MobileNumber = toHalfWidth(values.MobileNumber);
          tempData.Address = values.Address?.replace(values.PrefectureName, "");
          tempData.Email = values.Email?.toLowerCase();
          props.submitHandler(tempData);
        }}
      >
        {({ setFieldValue, values }) => {
          return (
            <div className="container-fluid mt-4 p-0">
              <Form className="user-form">
                {props.getStoreManagementLoading ? (
                  <FormLoader loading={props.getStoreManagementLoading} />
                ) : null}
                <FormObserver
                  prefectureByZipCode={prefectureByZipCode}
                  initialPostalCode={props.initialValues.PostalCode}
                />

                {/* Row 1 */}
                <div className="form-group row">
                  <div className="col-sm-4 mb-3">
                    <Label
                      label="store_name"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="StoreName"
                      type="text"
                      as={FormikFormInput}
                      // invalidcharacter={inValidCharacterForStoreName}
                      placeholder="enter_store_name"
                      disabled
                    />
                  </div>

                  <div className="col-sm-4 mb-3">
                    <Label
                      label="store_name_kana"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="KanaName"
                      type="text"
                      placeholder="enter_store_name_kana"
                      // regex={stringJpRegex}
                      as={FormikFormInput}
                      disabled
                    />
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label
                      label="store_owner_name"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="StoreOwnerName"
                      type="text"
                      as={FormikFormInput}
                      // invalidcharacter={inValidCharacterForStoreName}
                      placeholder="enter_store_owner_name"
                    />
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label
                      label="store_code"
                      required={false}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="StoreCode"
                      type="text"
                      placeholder="enter_store_code"
                      as={FormikFormInput}
                      disabled={props.id > 0}
                    />
                  </div>
                  <div className="col-sm-8 mb-3">
                    <Label
                      label="store_category"
                      required={false}
                      className="label-style"
                    />
                    {/* <Field
                      name="StoreCategory"
                      as={FormikFormSelect}
                      options={props.storeCategoryOptions}
                      disabled
                    /> */}
                    <div className="row">
                        <div className="col-6">
                          <Field
                            type="radio"
                            name="StoreCategory"
                            value="0"
                            id="fcStore"
                            className="form-check-input"
                            disabled="disabled"
                          />
                          <label className="form-check-label" htmlFor="fcStore">
                            <FormattedMessage id="fc_store" />
                          </label>
                        </div>
                        <div className="col-6">
                          <Field
                            type="radio"
                            name="StoreCategory"
                            value="1"
                            id="dmStore"
                            className="form-check-input"
                            disabled="disabled"
                          />
                          <label className="form-check-label" htmlFor="dmStore">
                            <FormattedMessage id="dm_store" />
                          </label>
                        </div>
                      </div>
                  </div>

                  {/* <div className="col-sm-4 mb-3">
                    <Label
                      label="business_type"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      name="BusinessTypeId"
                      as={FormikFormSelect}
                      options={props.businessTypeOptions}
                      disabled
                    />
                  </div> */}

                  <div className="col-sm-6 mb-3">
                    <Label
                      label="email"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="Email"
                      type="text"
                      placeholder="enter_email"
                      as={FormikFormInput}
                      validcharacter={["@", "#", "$", "-", "_", "."]}
                    />
                  </div>
                  <div className="col-sm-6 mb-3">
                    <Label
                      label="postal_code"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="PostalCode"
                      type="text"
                      placeholder="enter_post_code"
                      regex={integerNumJpRegex}
                      setState={setPostalCode}
                      as={FormikFormInputForRegexCheck}
                    />
                  </div>
                  <div className="col-sm-12 mb-3">
                      
                    <Label
                      label="address"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="Address"
                      type="text"
                      placeholder="enter_address"
                      prefectureName={values?.PrefectureName}
                      validcharacter={[
                        "@",
                        "#",
                        "$",
                        "-",
                        "_",
                        ".",
                        "/",
                        ",",
                        "%",
                        "!",
                        "&",
                      ]}
                      as={FormikFormInputAddress}
                    />
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label
                      label="contact_number"
                      required={true}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="MobileNumber"
                      type="text"
                      autoComplete="new-mobile-number"
                      placeholder="enter_mobile_number"
                      regex={integerRegex}
                      as={FormikFormInputMobileNumber}
                    />
                  </div>
                  
                  <div className="col-sm-8 mb-3">
                    <Label
                      label="map_url"
                      required={false}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="Url"
                      type="text"
                      validcharacter={[
                        "@",
                        "#",
                        "$",
                        "-",
                        "_",
                        ".",
                        "/",
                        ",",
                        "%",
                        "!",
                        "&",
                        "=",
                        "+",
                        "?",
                        ":",
                      ]}
                      // invalidcharacter={[" "]}
                      placeholder="enter_map_url"
                      as={FormikFormInput}
                    />
                  </div>
                  

                  <div className="col-sm-12 mb-3">
                    <Label
                      label="reservation_site"
                      required={false}
                      className="label-style"
                    />
                    <Field
                      className="form-control"
                      name="StoreLink"
                      type="text"
                      placeholder="enter_reservation_site"
                      as={FormikFormInput}
                      disabled
                    />
                  </div>
                  <div className="col-sm-6 mb-3">
                        <Label
                          className="label-style"
                          required={false}
                          label="password"
                        />
                        <div className="form-group">
                          <Field
                            className="form-control"
                            name="Password"
                            autoComplete="new-password"
                            placeholder="enter_new_password"
                            invalidcharacter={[" "]}
                            validcharacter={[
                              "~",
                              "`",
                              "!",
                              "@",
                              "#",
                              "$",
                              "%",
                              "^",
                              "&",
                              "*",
                              "(",
                              ")",
                              "_",
                              "-",
                              "+",
                              "=",
                              "{",
                              "[",
                              "}",
                              "]",
                              "|",
                              "\\",
                              ":",
                              ";",
                              "'",
                              '"',
                              "<",
                              ",",
                              ">",
                              ".",
                              "?",
                              "/",
                            ]}
                            as={FormikFormPasswordInput}
                          />
                        </div>
                      </div>
                      <div className="col-sm-6 mb-3 form-group">
                        <Label
                          className="label-style"
                          required={false}
                          label="confirm_password"
                        />
                        <div className="form-group">
                          <Field
                            className="form-control"
                            autocomplete="new-password"
                            name="ConfirmPassword"
                            placeholder="enter_confirm_password"
                            as={FormikFormPasswordInput}
                            invalidcharacter={[" "]}
                            validcharacter={[
                              "~",
                              "`",
                              "!",
                              "@",
                              "#",
                              "$",
                              "%",
                              "^",
                              "&",
                              "*",
                              "(",
                              ")",
                              "_",
                              "-",
                              "+",
                              "=",
                              "{",
                              "[",
                              "}",
                              "]",
                              "|",
                              "\\",
                              ":",
                              ";",
                              "'",
                              '"',
                              "<",
                              ",",
                              ">",
                              ".",
                              "?",
                              "/",
                            ]}
                          />
                        </div>
                      </div>
                  
                  <div className="col-sm-12 mb-3">
                    <Label
                      label="nomination_fee"
                      required={false}
                      className="label-style"
                    />
                    <div className="row">
                      <div className="col-lg-3 col-sm-4 col-6">
                        <Field
                          type="radio"
                          name="IsNominationFee"
                          value="1"
                          id="include"
                          className="form-check-input"
                        />
                        <label htmlFor="include" className="form-check-label">
                          <FormattedMessage id="include" />
                        </label>
                      </div>
                      <div className="col-lg-3 col-sm-4 col-6">
                        <Field
                          type="radio"
                          name="IsNominationFee"
                          value="0"
                          id="notInclude"
                          className="form-check-input"
                        />
                        <label
                          htmlFor="notInclude"
                          className="form-check-label"
                        >
                          <FormattedMessage id="not_include" />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-4 mb-3">
                    {/* <Label label="upload_image" required={false} className="label-style d-block pb-0" /> */}
                    <Field
                      className="form-control"
                      name="Image"
                      type="text"
                      as={FormikFormImagePicker}
                    />
                  </div>
                </div>
                {/* Footer Buttons */}
                <div className="text-center">
                  <LoaderButton
                    type="submit"
                    iconName="bi-check-lg"
                    isLoading={props.addUpdateStoreManagementLoading}
                    text={props.id > -1 ? "button_update" : "button_register"}
                  />
                </div>
              </Form>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default DeletePopupHOC(StoreDetailsForm);
